// Category composition — part-to-whole across classifications.
// Categorical colour (the classes ARE the subject), horizontal stacked bar because
// the class names are long. Legend is mandatory at >= 2 series, and interior
// segments carry no inline label (they have no free end) — the legend, tooltip and
// table view carry those values instead.
import { useState } from 'react';
import { useChart, tooltipStyle, focusSeries } from '../lib/useChart';
import { chrome, classificationColor, CLASSIFICATION_ORDER } from '../lib/palette';
import { titleCase, esc } from '../lib/format';
import ChartCard from './ChartCard';

export default function CategoryComposition({ categories, mode }) {
  const [showTable, setShowTable] = useState(false);
  const [hovered, setHovered] = useState(null);
  const c = chrome(mode);
  // Hooks must run unconditionally — switching latest (has rows) → older fixture
  // runs (empty) used to early-return before useChart and blank the whole app.
  const empty = !categories?.length;

  // Iterate the FIXED order, not the data's order, so a filter can never repaint.
  const present = empty
    ? []
    : CLASSIFICATION_ORDER.filter((k) => categories.some((x) => x.classification === k));
  const total = empty ? 0 : categories.reduce((n, x) => n + x.tests_blocked, 0);

  const [ref, chartRef] = useChart(
    () => ({
      backgroundColor: 'transparent',
      grid: { left: 0, right: 0, top: 8, bottom: 8, containLabel: false },
      tooltip: {
        trigger: 'item',
        ...tooltipStyle(c),
        formatter: (p) => {
          const row = categories.find((x) => x.classification === p.seriesName);
          const share = total ? Math.round((row.tests_blocked / total) * 100) : 0;
          return (
            `<div style="font-weight:600;font-size:14px">${esc(row.tests_blocked)} tests · ${esc(share)}%</div>` +
            `<div style="color:${c.textSecondary}">${esc(titleCase(p.seriesName))} · ` +
            `${esc(row.clusters)} signal${row.clusters === 1 ? '' : 's'}</div>`
          );
        },
      },
      xAxis: { type: 'value', show: false, max: total || 1 },
      yAxis: { type: 'category', show: false, data: [''] },
      series: present.map((key, i) => {
        const row = categories.find((x) => x.classification === key);
        return {
          name: key,
          type: 'bar',
          stack: 'composition',
          data: [row.tests_blocked],
          barMaxWidth: 24,
          itemStyle: {
            color: classificationColor(key, mode),
            // 2px surface gap between segments — white does the separating, not a stroke.
            borderColor: c.surface,
            borderWidth: 1,
            borderRadius: i === present.length - 1 ? [0, 4, 4, 0] : 0,
          },
          // The hovered mark lifts, so the reader sees it respond. A surface-coloured
          // ring keeps the lift legible where segments touch.
          emphasis: {
            focus: 'self',
            itemStyle: { borderColor: c.textPrimary, borderWidth: 1.5, opacity: 1 },
          },
          blur: { itemStyle: { opacity: 0.35 } },
        };
      }),
    }),
    [categories],
    { mode }
  );

  // An empty plot area reads as broken. Say there is nothing, don't draw nothing.
  // useChart's effect no-ops when the chart div is not mounted (ref.current is null).
  if (empty) {
    return (
      <ChartCard title="What kind of failure" subtitle="Nothing blocked in this scope">
        <p className="chart-empty">No open signals to classify.</p>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="What kind of failure"
      subtitle={`${total} tests blocked across ${categories.length} classes`}
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr><th>Classification</th><th className="num">Signals</th><th className="num">Tests blocked</th></tr>
          </thead>
          <tbody>
            {present.map((key) => {
              const row = categories.find((x) => x.classification === key);
              return (
                <tr key={key}>
                  <td><span className="swatch" style={{ background: classificationColor(key, mode) }} aria-hidden="true" />{titleCase(key)}</td>
                  <td className="num">{row.clusters}</td>
                  <td className="num">{row.tests_blocked}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="chart chart--stack" />
      {/* Legend is the dependable identity channel — never colour-matching alone.
          It also DRIVES the chart: hovering or focusing a key lifts its segment, so
          a 1-test sliver is findable without hunting for it with the pointer. */}
      <ul className="legend">
        {present.map((key) => {
          const row = categories.find((x) => x.classification === key);
          const isDimmed = hovered !== null && hovered !== key;
          return (
            <li
              key={key}
              className={`is-interactive${isDimmed ? ' is-dimmed' : ''}`}
              tabIndex={0}
              onMouseEnter={() => { setHovered(key); focusSeries(chartRef, key, true); }}
              onMouseLeave={() => { setHovered(null); focusSeries(chartRef, key, false); }}
              onFocus={() => { setHovered(key); focusSeries(chartRef, key, true); }}
              onBlur={() => { setHovered(null); focusSeries(chartRef, key, false); }}
            >
              <span className="swatch" style={{ background: classificationColor(key, mode) }} aria-hidden="true" />
              <span className="legend__label">{titleCase(key)}</span>
              <span className="legend__value">{row.tests_blocked}</span>
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
}
